import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import cn from 'classnames';
import { Column, ConfirmationModal, IconButton, Row, TextField, Typography, useOverlayContext } from 'seagull';
import { ColorThumbDown, ColorThumbUp, ThumbDown, ThumbUp } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import styles from './FeatureFeedbackModal.module.scss';
export const FEATURE_FEEDBACK_COMMENT_MAX_LENGTH = 500;
export const GetFeatureFeedbackModalTID = (parentTestId)=>getTestIds(parentTestId);
export const FeatureFeedbackModal = (param)=>{
    let { leadingText, supportingText, onSubmit, onClose, testId } = param;
    const { t } = useLocaleKeys('common.featureFeedback');
    const { t: errorTexts } = useLocaleKeys('errors');
    const { showSuccessToast, showErrorToast } = useOverlayContext();
    const { FeatureFeedbackModalTestIds } = useMemo(()=>GetFeatureFeedbackModalTID(testId), [
        testId
    ]);
    const [rating, setRating] = useState(null);
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const submit = async (chosenRating)=>{
        setSubmitting(true);
        try {
            await onSubmit({
                rating: chosenRating,
                comment: comment.trim()
            });
            showSuccessToast(t.successToast(), FeatureFeedbackModalTestIds.SuccessToast());
            onClose();
        } catch (unused) {
            setSubmitting(false);
            showErrorToast(errorTexts.unexpected(), FeatureFeedbackModalTestIds.ErrorToast());
        }
    };
    const commentLabel = rating === 'thumbsDown' ? t.commentLabelNegative() : t.commentLabelPositive();
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        testId: FeatureFeedbackModalTestIds.Root(),
        bi: {
            primaryButton: {
                name: 'feature_feedback_submit'
            },
            secondaryButton: {
                name: 'feature_feedback_close'
            },
            closeButton: {
                name: 'feature_feedback_close'
            }
        },
        skin: "transparent",
        width: "420px",
        title: t.title(),
        primaryButtonLabel: t.submit(),
        primaryButtonDisabled: rating == null,
        primaryButtonLoading: submitting,
        secondaryButtonLabel: t.close(),
        onPrimaryButtonClick: rating == null ? undefined : ()=>void submit(rating),
        onSecondaryButtonClick: onClose,
        onCloseButtonClick: onClose,
        content: /*#__PURE__*/ _jsxs(Column, {
            gap: 16,
            alignItems: "center",
            children: [
                /*#__PURE__*/ _jsxs(Column, {
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            children: leadingText
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            children: supportingText
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Row, {
                    gap: 12,
                    justifyContent: "center",
                    children: [
                        /*#__PURE__*/ _jsx(IconButton, {
                            bi: {
                                name: 'feature_feedback_thumbs_up'
                            },
                            testId: FeatureFeedbackModalTestIds.ThumbsUp(),
                            "aria-label": t.thumbsUp(),
                            size: "medium",
                            skin: "transparent",
                            pressed: rating === 'thumbsUp',
                            className: cn(styles.thumb, {
                                [styles.thumbDimmed]: rating === 'thumbsDown'
                            }),
                            onClick: ()=>setRating('thumbsUp'),
                            children: rating === 'thumbsUp' ? /*#__PURE__*/ _jsx(ColorThumbUp, {
                                size: 24
                            }) : /*#__PURE__*/ _jsx(ThumbUp, {
                                size: 24
                            })
                        }),
                        /*#__PURE__*/ _jsx(IconButton, {
                            bi: {
                                name: 'feature_feedback_thumbs_down'
                            },
                            testId: FeatureFeedbackModalTestIds.ThumbsDown(),
                            "aria-label": t.thumbsDown(),
                            size: "medium",
                            skin: "transparent",
                            pressed: rating === 'thumbsDown',
                            className: cn(styles.thumb, {
                                [styles.thumbDimmed]: rating === 'thumbsUp'
                            }),
                            onClick: ()=>setRating('thumbsDown'),
                            children: rating === 'thumbsDown' ? /*#__PURE__*/ _jsx(ColorThumbDown, {
                                size: 24
                            }) : /*#__PURE__*/ _jsx(ThumbDown, {
                                size: 24
                            })
                        })
                    ]
                }),
                rating != null && /*#__PURE__*/ _jsx(TextField, {
                    bi: {
                        name: 'feature_feedback_comment'
                    },
                    testId: FeatureFeedbackModalTestIds.Comment(),
                    multiline: true,
                    minRows: 3,
                    width: "100%",
                    label: commentLabel,
                    value: comment,
                    onChange: (event)=>setComment(event.target.value),
                    inputProps: {
                        maxLength: FEATURE_FEEDBACK_COMMENT_MAX_LENGTH
                    }
                })
            ]
        })
    });
};
